import { Trigger as RadixTrigger } from '@radix-ui/react-dropdown-menu';
import {
  forwardRef,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { isElement } from 'react-is';

import {
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';

import { useMenuContext } from './hooks/useMenuContext.js';
import { useIsInViewport } from '../../core/hooks/useIsInViewport.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';

/**
 * @public
 */
export interface MenuTriggerProps extends WithChildren, AriaLabelingProps {}

/**
 * Allows to check if a ReactNode is a MenuTrigger element.
 * @internal
 * */
export function isTrigger(
  node: ReactNode,
): node is ReactElement<MenuTriggerProps> {
  return (
    node !== undefined &&
    node !== null &&
    isElement(node) &&
    node.type === Trigger
  );
}

/**
 * The button that toggles the dropdown menu.
 * By default, the Content will position itself against the trigger.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  MenuTriggerProps
>(
  (
    {
      children,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      ...remainingProps
    },
    forwardedRef,
  ) => {
    /** If the menu trigger is not visible in the viewport, close the menu to avoid issues with overlay drawing loop */
    const [containerRef, isVisible] = useIsInViewport<HTMLButtonElement>();
    const rootRef = useMergeRefs([forwardedRef, containerRef]);

    const { isOpen, setIsOpen, isDefaultOpen } = useMenuContext();
    // Flag to indicate if the menu was forcibly closed once already or not.
    // This would allow the menu to be closed only once if defaultOpen was true and
    // the Menu trigger wasn't visible to avoid page crashes due to infinite calls to window.render.
    // And to also avoid closing the menu repeatedly due to issues with virtualization.
    const [isForciblyClosed, setIsForciblyClosed] = useState(false);

    useEffect(() => {
      if (!isVisible && isDefaultOpen && isOpen && !isForciblyClosed) {
        setIsOpen(false);
        setIsForciblyClosed(true);
      }
    }, [isVisible, isOpen, setIsOpen, isForciblyClosed, isDefaultOpen]);

    return (
      <RadixTrigger
        {...remainingProps}
        ref={rootRef}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        data-testid="menu-trigger"
        asChild
      >
        {children}
      </RadixTrigger>
    );
  },
);

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'Menu.Trigger';
